import React, { useMemo, useState } from "react";
import { useMutation } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { Loading, Box, Button, Divider, Heading } from "@foris/avocado-suite";
import { instructorSearchMutation } from "../graphql/advanceSearch.queries";
import useNavigationUrl from "@common/hooks/useNavigationUrl";
import useContextUrl from "@common/hooks/useContextUrl";
import AdvancedSelectorsWrapper from "@shared/ui/advance-search/advanced-selectors-wrapper";
import { ContractType } from "@modules/shared/selectors";
import { Blocks, TimeRange } from "@modules/shared/inputs";
import { Days, Fictional } from "@modules/shared/checks";
import EmptyResultsModal from "@modules/shared/empty-results-modal";
import WeeksRange from "@modules/shared/weeksRange/WeeksRange";
import { objectEmpty } from "@utils/validations";
import { validations } from "@services/collections/advance-search";
import {
  IBlocksDependency,
  ITimeRangeDependency,
  IWeeksRangeDependency,
} from "@services/types/advance-search/advanceSearchTypes";
import css from "./advanceSearch.module.scss";
import {
  setAdvancedSearchAssignedBlocks,
  setAdvancedSearchBlockRange,
  setAdvancedSearchContractType,
  setAdvancedSearchDays,
  setAdvancedSearchDaysError,
  setAdvancedSearchDummyInstructors,
  setAdvancedSearchError,
  setAdvancedSearchIntervalRange,
  setAdvancedSearchIntervals,
  setAdvancedSearchTimeRangeError,
  useAdvancedSearchStore,
} from "@context/useAdvancedSearchStore";
import { useSharedAdvancedSearchStore } from "@dimensions/hooks/useSharedAdvancedSearchStore";

const AdvanceSearch: React.FC<any> = (props: any) => {
  // States for validations
  const [isEmpty, setIsEmpty] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectorsState, setSelectorsState] = useState({
    campus: null,
    department: null,
    modality: null,
    shift: null,
    program: null,
    curriculum: null,
    level: null,
    course: null,
  });
  // Hooks for handling navigation
  const { advancedSearchResultsUrl } = useNavigationUrl();
  const { origin, scenario } = useContextUrl();
  const history = useHistory();
  const [advanceSearchMutation] = useMutation(instructorSearchMutation, {});
  const { objAvailability, daysError, timeRangeError, error, intervalRange, blockRange } =
    useSharedAdvancedSearchStore();
  const objAssignedBlocks = useAdvancedSearchStore(state => state.objAssignedBlocks);

  const dummyInstructors = useAdvancedSearchStore(state => state.dummyInstructors);
  const objDummy = useMemo(() => {
    if (!dummyInstructors) return {};

    return validations.dummy(dummyInstructors, {});
  }, [dummyInstructors]);

  const contractType = useAdvancedSearchStore(state => state.contractType);
  const objContractType = useMemo(() => {
    if (!contractType) return {};

    return validations.contractType(contractType?.value, {});
  }, [contractType]);

  const hasSomeFilters = useMemo(() => {
    return (
      !objectEmpty(objContractType) ||
      !objectEmpty(objDummy) ||
      !objectEmpty(objAssignedBlocks) ||
      !objectEmpty(objAvailability) ||
      Object.values(selectorsState).some(value => !!value)
    );
  }, [objContractType, objDummy, objAssignedBlocks, objAvailability, selectorsState]);

  const setBlocks = (values: IBlocksDependency, error: boolean) => {
    if (error) {
      setAdvancedSearchError(error);
      return;
    }
    const validation = validations.blocks(values, objAssignedBlocks);
    setAdvancedSearchAssignedBlocks(validation.assignedBlocks);
    setAdvancedSearchError(validation.error);
  };

  const setDays = (value: number[]) => {
    const validation = validations.days(value, objAvailability);
    setAdvancedSearchDays(validation?.availability?.availability?.dayId ?? []);
    setAdvancedSearchError(validation.error);
    setAdvancedSearchTimeRangeError(validation.timeRangeError);
    setAdvancedSearchDaysError(validation.daysError);
  };

  const setTimeRange = (value: ITimeRangeDependency, error: boolean) => {
    if (error) {
      setAdvancedSearchError(true);
      return;
    }
    const validation = validations.timeRange(value, objAvailability);
    setAdvancedSearchBlockRange(validation?.availability?.availability?.blockRange);
    setAdvancedSearchError(validation.error);
    setAdvancedSearchTimeRangeError(validation.timeRangeError);
    setAdvancedSearchDaysError(validation.daysError);
  };

  const setWeeksRange = (values: IWeeksRangeDependency) => {
    const validation = validations.weeksRange(values, objAvailability);
    setAdvancedSearchIntervalRange(validation?.availability?.availability?.intervalRange ?? null);
    setAdvancedSearchIntervals(validation?.availability?.availability?.intervals ?? null);
  };

  let weeksDisabled = true;
  if (objAvailability.availability) {
    const daysExist = objAvailability.availability.hasOwnProperty("dayId");
    const rangeExist = objAvailability.availability.hasOwnProperty("blockRange");
    if (rangeExist) {
      const start = objAvailability.availability.blockRange.hasOwnProperty("start");
      const end = objAvailability.availability.blockRange.hasOwnProperty("end");
      if (start && end && daysExist) weeksDisabled = false;
    }
  }

  const createObjectMutation = () => {
    const filterArray = [];
    const programFilters = [];

    !objectEmpty(objContractType) && filterArray.push(objContractType);
    !objectEmpty(objDummy) && filterArray.push(objDummy);
    !objectEmpty(objAssignedBlocks) && filterArray.push(objAssignedBlocks);
    !objectEmpty(objAvailability) && filterArray.push(objAvailability);

    selectorsState.level && filterArray.push({ courseLevel: { eq: selectorsState.level } });
    selectorsState.course && filterArray.push({ courseId: { eq: selectorsState.course } });
    selectorsState.campus && programFilters.push({ campusId: { eq: selectorsState.campus } });
    selectorsState.modality && programFilters.push({ modalityId: { eq: selectorsState.modality } });
    selectorsState.department &&
      programFilters.push({ departmentId: { eq: selectorsState.department } });
    selectorsState.shift && programFilters.push({ shiftId: { eq: selectorsState.shift } });
    selectorsState.program && programFilters.push({ code: { eq: selectorsState.program } });

    if (programFilters.length > 0) {
      filterArray.push({
        program: {
          and: programFilters,
        },
      });
    }
    const mutationFilter = {
      originId: origin,
      scenarioId: scenario,
      filter: {
        fields: {
          and: [
            // Set default filter 'cause now we bring only the ones with assignment
            { hasEvents: true },
            ...filterArray,
          ],
        },
      },
      pagination: {
        page: 1,
        size: 10,
      },
    };
    return mutationFilter;
  };
  const callUpdateHook = async () => {
    setLoading(true);
    try {
      const variables = createObjectMutation();
      const { data } = await advanceSearchMutation({ variables });
      setLoading(false);
      const dataItems = data.filter.createInstructorFilter;
      if (dataItems.instructors.items.length > 0) {
        const idSearch = dataItems.id;
        history.push(advancedSearchResultsUrl("instructor", idSearch));
      } else {
        setIsEmpty(true);
      }
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  };

  const { t } = useTranslation();

  return (
    <div className={css.ctnAdvanceSearch}>
      <EmptyResultsModal show={isEmpty} onClose={() => setIsEmpty(false)} />

      <Box className={css.ctnAdvanceSearch_boxContent}>
        <AdvancedSelectorsWrapper
          className={css.hasBottomSeparation}
          selectors={["campus", "department", "modality", "shift", "program", "level", "course"]}
          scenario={scenario}
          onChangeState={setSelectorsState}
        />

        <Divider className={css.divider} />

        <ContractType
          className={css.typeContract}
          contractTypeOption={contractType}
          label={t("advanced-search.instructor.forms.select-contract-type.label")}
          placeholder={t("advanced-search.instructor.forms.select-contract-type.placeholder")}
          onCallback={values => setAdvancedSearchContractType(values ?? null)}
        />

        <Divider className={css.divider} />

        <Blocks
          minInitialValue={objAssignedBlocks?.assignedBlocks?.gte?.toString() ?? ""}
          maxInitialValue={objAssignedBlocks?.assignedBlocks?.lte?.toString() ?? ""}
          onCallback={(values, error) => {
            setBlocks(values, error);
          }}
        />

        <Divider className={css.divider} />

        <Fictional
          value={dummyInstructors}
          onCallback={value => setAdvancedSearchDummyInstructors(value)}
        />

        <Divider className={css.divider} />
        <Heading type="h4">{t("advanced-search.instructor.forms.days.title")}</Heading>
        <Days
          initialValue={objAvailability?.availability?.dayId ?? []}
          required={daysError}
          onCallback={values => setDays(values)}
        />
        <Divider className={css.divider} />

        <Heading type="h4">{t("advanced-search.instructor.forms.time-range.title")}</Heading>

        <TimeRange
          minInitialValue={blockRange?.start ?? ""}
          maxInitialValue={blockRange?.end ?? ""}
          required={timeRangeError}
          onCallback={(values, error) => setTimeRange(values, error)}
        />
        <Divider className={css.divider} />

        <WeeksRange
          {...props}
          initialExternalWeeks={
            intervalRange
              ? {
                  min: intervalRange?.start ?? 1,
                  max: intervalRange?.end ?? 1,
                }
              : undefined
          }
          disabled={weeksDisabled}
          onCallback={value => setWeeksRange(value)}
        />
      </Box>

      {loading ? (
        <Loading />
      ) : (
        <Button
          className={css.ctnAdvanceSearch_actionButton}
          disabled={error || !hasSomeFilters}
          onClick={() => callUpdateHook()}
        >
          {t("advanced-search.instructor.forms.btn-search")}
        </Button>
      )}
    </div>
  );
};

export default AdvanceSearch;
